// ---------------------------------------------------------------------------
// DnD Email Editor V2 – Social links modal
//
// Shown when a Social block is first added (dragged/clicked/added to a column)
// and again on double-click of a social layer/element. Edits which platforms
// are shown and their URLs (with the facility merge-tag quick button), plus the
// "Icon Styles" (which social logo set supplies the icons). Icon size, colors
// and alignment stay in the inspector. Confirming commits the changes; Cancel /
// Esc / backdrop discards.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Braces, ChevronDown } from 'lucide-react';
import type { SocialLink, SocialLogos, SocialPlatform } from './dnd-model';
import { SOCIAL_PLATFORM_VARIABLES, SOCIAL_PLATFORM_LOGO_KEYS, logosFromSet } from './dnd-defaults';
import { useSocialLogoSets } from '@/hooks/useSocialLogoSets';
import type { SocialLogoSet } from '@/services/socialLogoSets.service';

/** What the modal commits back: the edited links plus the chosen logo set and
 *  its snapshotted per-platform PNG URLs (see SocialLogos). */
export interface SocialModalResult {
  links: SocialLink[];
  logoSetId: number | null;
  logos: SocialLogos;
}

export interface DndSocialModalProps {
  isOpen: boolean;
  /** Links of the block being added/edited, used to seed the modal. */
  initialLinks: SocialLink[];
  /** Currently selected logo set of the block, used to seed the icon-style picker. */
  initialLogoSetId?: number | null;
  /** Effective background the block renders on, so the icon preview is true-to-life. */
  previewBg?: string;
  /** Label for the confirm button — "Add Social Links" on add, "Apply" on edit. */
  confirmLabel?: string;
  /** Commit the edited links + icon style to the model, then close. */
  onSave: (result: SocialModalResult) => void;
  /** Discard changes (or abort the add) and close. */
  onClose: () => void;
}

const ALL_PLATFORMS = Object.keys(SOCIAL_PLATFORM_LOGO_KEYS) as SocialPlatform[];

// Preview icon size. Rendered with explicit width/height attributes (like the
// editor canvas) so the bitmap isn't resampled and stays crisp.
const PREVIEW_ICON_SIZE = 30;

/** The set's PNG icons for the given platforms, as <img> chips. */
function renderSetIcons(set: SocialLogoSet | undefined, platforms: SocialPlatform[]) {
  if (!set) return null;
  const setLogos = logosFromSet(set);
  return platforms.map((p) => {
    const url = setLogos[p];
    return url ? (
      <img
        key={p}
        src={url}
        alt={p}
        width={PREVIEW_ICON_SIZE}
        height={PREVIEW_ICON_SIZE}
        className="dnd-social-style-icon"
      />
    ) : null;
  });
}

export function DndSocialModal({
  isOpen,
  initialLinks,
  initialLogoSetId,
  previewBg,
  confirmLabel = 'Apply',
  onSave,
  onClose,
}: DndSocialModalProps) {
  const [links, setLinks] = useState<SocialLink[]>(initialLinks);
  const [logoSetId, setLogoSetId] = useState<number | null>(initialLogoSetId ?? null);
  // The icon-style picker is a dropdown: the trigger shows the selected set's
  // icons; clicking it opens an overlay listing the sets to choose from.
  const [stylesOpen, setStylesOpen] = useState(false);
  const styleSelectRef = useRef<HTMLDivElement>(null);

  const { data: logoSets } = useSocialLogoSets();
  const hasSets = !!logoSets?.length;

  // Re-seed from the block each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setLinks(initialLinks.map((l) => ({ ...l })));
      setLogoSetId(initialLogoSetId ?? null);
      setStylesOpen(false);
    }
  }, [isOpen, initialLinks, initialLogoSetId]);

  // A logo set is always selected: the built-in glyphs are inline SVG, which
  // Outlook/Gmail strip, so there is no "none" option. Auto-pick the first set
  // once sets load if the block has no valid one (mirrors the inspector).
  useEffect(() => {
    if (!isOpen || !logoSets?.length) return;
    const valid = logoSetId != null && logoSets.some((s) => s.socialLogoSetID === logoSetId);
    if (!valid) setLogoSetId(logoSets[0].socialLogoSetID);
  }, [isOpen, logoSets, logoSetId]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Esc closes the open dropdown first, then the modal.
      if (stylesOpen) setStylesOpen(false);
      else onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, stylesOpen]);

  // Close the icon-style dropdown when clicking anywhere outside it. Capture
  // phase so the dialog's own stopPropagation doesn't swallow the event.
  useEffect(() => {
    if (!stylesOpen) return;
    const onDown = (e: MouseEvent) => {
      if (styleSelectRef.current && !styleSelectRef.current.contains(e.target as Node)) {
        setStylesOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [stylesOpen]);

  // Preview the icons that will actually appear: the enabled platforms, or all
  // of them when nothing is enabled yet, so each style row shows something.
  const previewPlatforms = useMemo<SocialPlatform[]>(() => {
    const enabled = links.filter((l) => l.enabled).map((l) => l.platform);
    return enabled.length ? enabled : ALL_PLATFORMS;
  }, [links]);

  const selectedSet = logoSets?.find((s) => s.socialLogoSetID === logoSetId);
  const chipBg = previewBg || '#ffffff';

  if (!isOpen) return null;

  const updateLink = (idx: number, patch: Partial<SocialLink>) => {
    setLinks((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const handleSave = () => {
    const set = logoSets?.find((s) => s.socialLogoSetID === logoSetId);
    onSave({ links, logoSetId, logos: set ? logosFromSet(set) : {} });
  };

  return createPortal(
    <div
      className="dnd-html-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="dnd-html-modal dnd-simple-modal"
        role="dialog"
        aria-labelledby="dnd-social-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="dnd-html-modal-header">
          <h3 id="dnd-social-modal-title">Social links</h3>
          <button type="button" className="dnd-html-modal-close" title="Close" aria-label="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="dnd-simple-modal-body">
          <p className="dnd-simple-modal-hint">
            Turn platforms on or off and set each link. Use the <Braces size={11} style={{ verticalAlign: 'middle' }} /> button
            to fill in the facility's saved link. Icon size, colors and alignment live in the inspector.
          </p>

          <div className="dnd-social-modal-section-label">Icon Styles</div>
          {hasSets ? (
            <div className="dnd-social-style-select" ref={styleSelectRef}>
              {/* Trigger: just the selected set's icons + a chevron. */}
              <button
                type="button"
                className="dnd-social-style-trigger"
                aria-haspopup="listbox"
                aria-expanded={stylesOpen}
                aria-label="Icon style"
                onClick={() => setStylesOpen((o) => !o)}
              >
                <span className="dnd-social-style-icons" style={{ background: chipBg }}>
                  {renderSetIcons(selectedSet, previewPlatforms)}
                </span>
                <ChevronDown size={16} className="dnd-social-style-caret" />
              </button>
              {/* Overlay: one icon-only row per set. */}
              {stylesOpen && (
                <div className="dnd-social-style-menu" role="listbox" aria-label="Icon style">
                  {logoSets!.map((set) => {
                    const selected = set.socialLogoSetID === logoSetId;
                    return (
                      <button
                        key={set.socialLogoSetID}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`dnd-social-style-option${selected ? ' selected' : ''}`}
                        onClick={() => {
                          setLogoSetId(set.socialLogoSetID);
                          setStylesOpen(false);
                        }}
                      >
                        <span className="dnd-social-style-icons" style={{ background: chipBg }}>
                          {renderSetIcons(set, previewPlatforms)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <p className="dnd-social-modal-styles-empty">
              No icon styles are configured. Icons fall back to non–email-safe glyphs until a set is added in settings.
            </p>
          )}

          <div className="dnd-social-modal-section-label">Links</div>
          <div className="dnd-social-modal-list">
            {links.map((link, idx) => {
              const variable = SOCIAL_PLATFORM_VARIABLES[link.platform];
              return (
                <div key={link.platform} className={`dnd-social-modal-row${link.enabled ? '' : ' disabled'}`}>
                  <label className="dnd-social-modal-toggle">
                    <input
                      type="checkbox"
                      checked={link.enabled}
                      onChange={(e) => updateLink(idx, { enabled: e.target.checked })}
                    />
                    <span className="dnd-social-modal-platform">{link.platform}</span>
                  </label>
                  <input
                    type="text"
                    className="dnd-social-modal-url"
                    value={link.url}
                    onChange={(e) => updateLink(idx, { url: e.target.value })}
                    placeholder={`${link.platform} URL`}
                    disabled={!link.enabled}
                  />
                  {variable && (
                    <button
                      type="button"
                      className="dnd-social-modal-var-btn"
                      title={`Use facility ${link.platform} link (${variable})`}
                      aria-label={`Use facility ${link.platform} link`}
                      onClick={() => updateLink(idx, { url: variable })}
                      disabled={!link.enabled}
                    >
                      <Braces size={13} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="dnd-html-modal-footer">
          <button type="button" className="dnd-html-modal-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="dnd-html-modal-btn dnd-html-modal-btn-primary" onClick={handleSave}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
